import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Building2, Hammer, Droplets, ArrowRight } from 'lucide-react';
import { projectService } from '../services/api';

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await projectService.getAll();
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIconForCategory = (category) => {
    const iconMap = {
      'Building': Building2,
      'Water': Droplets,
      'Roads': Hammer,
      'Civil Works': Hammer,
      'Infrastructure': Building2
    };
    return iconMap[category] || Building2;
  };

  const filters = ['All', 'Building', 'Civil Works', 'Roads', 'Water', 'Infrastructure'];

  const filteredProjects = activeFilter === 'All' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Our Projects - BURALE Maintainance & Suppliers | Portfolio & Experience</title>
        <meta name="description" content="Explore our portfolio of completed projects across construction, roads, water infrastructure and specialized developments in Kenya." />
      </Helmet>
      
      <section className="relative h-96 flex items-center justify-center bg-gradient-to-br from-burale-dark to-burale-teal">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">OUR PROJECTS</h1>
          <p className="text-2xl text-burale-gold font-semibold mb-4">Experience That Speaks Through Delivery</p>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Explore selected projects demonstrating our experience across construction, roads, water infrastructure and specialized developments.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  activeFilter === filter
                    ? 'bg-burale-teal text-white'
                    : 'bg-gray-100 text-burale-dark hover:bg-gray-200'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-burale-teal"></div>
              <p className="text-gray-600 mt-4">Loading projects...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProjects.map((project, index) => {
                  const Icon = getIconForCategory(project.category);
                  return (
                    <div
                      key={project.id || index}
                      className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group"
                    >
                      <div className="h-64 bg-gradient-to-br from-burale-teal to-burale-dark flex items-center justify-center relative overflow-hidden">
                        <Icon className="text-white/20 group-hover:scale-110 transition-transform duration-500" size={120} />
                        <div className="absolute top-4 right-4 bg-burale-gold text-burale-dark px-4 py-2 rounded-lg font-semibold text-sm">
                          {project.year}
                        </div>
                      </div>
                      <div className="p-6">
                        <span className="text-burale-teal font-semibold text-sm uppercase tracking-wider">
                          {project.category}
                        </span>
                        <h3 className="text-2xl font-bold text-burale-dark mt-2 mb-2">
                          {project.title}
                        </h3>
                        <p className="text-gray-500 text-sm mb-4">{project.location}</p>
                        <p className="text-gray-600 leading-relaxed">
                          {project.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {filteredProjects.length === 0 && !loading && (
                <div className="text-center py-20">
                  <p className="text-gray-500 text-xl">No projects found in this category.</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <section className="py-20 bg-burale-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-12 shadow-xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold text-burale-teal mb-2">50+</div>
                <div className="text-gray-600 font-medium">Projects Completed</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-burale-teal mb-2">15+</div>
                <div className="text-gray-600 font-medium">Years Experience</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-burale-teal mb-2">100%</div>
                <div className="text-gray-600 font-medium">Client Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-burale-teal text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Let's Build Your Next Project</h2>
          <p className="text-xl mb-8 leading-relaxed">
            Ready to turn your vision into reality? Contact us to discuss your project requirements.
          </p>
          <a
            href="/contact"
            className="bg-white text-burale-teal px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 inline-flex items-center text-lg"
          >
            Get Started <ArrowRight className="ml-2" size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Projects;
